<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card class="md-card-stats">
        <md-card-header class="md-card-header-icon md-card-header-primary">
          <div class="card-icon">
            <md-icon>bar_chart</md-icon>
          </div>
          <md-toolbar class="fm-card-header">
            <h1 class="md-title">
              Showing Telemetry for Client Connection: {{ id }}
              <badge id="progress" type="info" :style="backgroundColour"
                >{{ quality }}%</badge
              >
            </h1>
          </md-toolbar>
        </md-card-header>
      </md-card>
    </div>
    <div class="md-layout-item md-size-100">
      <nav-tabs-card>
        <template slot="content">
          <md-tabs md-sync-route class="md-primary" md-alignment="left">
            <md-tab id="tab-video" md-label="Video" md-icon="videocam">
              <div class="md-layout">
                <div
                  v-for="chart in charts"
                  :key="chart.key"
                  class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25"
                >
                  <time-series
                    :ref="`video-${chart.key}`"
                    v-bind:metadata="chart"
                  ></time-series>
                </div>
              </div>
            </md-tab>
            <md-tab id="tab-audio" md-label="Audio" md-icon="keyboard_voice">
              <div class="md-layout">
                <div
                  v-for="chart in nonVideoCharts"
                  :key="chart.key"
                  class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25"
                >
                  <time-series
                    :ref="`audio-${chart.key}`"
                    v-bind:metadata="chart"
                  ></time-series>
                </div>
              </div>
            </md-tab>
          </md-tabs>
        </template>
      </nav-tabs-card>
    </div>
  </div>
</template>
<script>
import api from "@/api";
import { NavTabsCard, Badge } from "@/components";
import TimeSeries from "./TimeSeriesChart";
export default {
  components: { NavTabsCard, Badge, TimeSeries },
  data() {
    return {
      red: 0,
      green: 100,
      bootstrapped: false,
      freshnessTimerInterval: 30000, // We check if the data is fresh (and notify when it isn't).
      freshnessIntervalId: null,
      queryTag: "connection_stats",
      lastTimestamp: this.$store.getters.sqlTimestampGenerator(1), // one day ago
      connectionStatsDelay: 500,
      connectionStatsRunning: true,
      lastPacketsLostValue: 0,
      lastPacketsReceivedValue: 0,
      supportedStreamTypes: ["audio", "video"],
      charts: [
        {
          key: "bytesSent",
          title: "Bytes Sent",
          videoOnly: false,
          senderOnly: true,
          unit: "bytes"
        },
        {
          key: "bytesReceived",
          title: "Bytes Received",
          videoOnly: false,
          receiverOnly: true,
          unit: "bytes"
        },
        {
          key: "packetsSent",
          title: "Packets Sent",
          videoOnly: false,
          senderOnly: true,
          unit: "packets"
        },
        {
          key: "packetsReceived",
          title: "Packets Received",
          videoOnly: false,
          receiverOnly: true,
          unit: "packets"
        },
        {
          key: "packetsLost",
          title: "Packets Lost",
          videoOnly: false,
          receiverOnly: true,
          unit: "packets"
        },
        {
          key: "nackCount",
          title: "NACK Count",
          videoOnly: true,
          unit: "nacks"
        },
        {
          key: "pliCount",
          title: "PLI Count",
          videoOnly: true,
          unit: "PLIs"
        },
        {
          key: "firCount",
          title: "FIR Count",
          videoOnly: true,
          unit: "FIRs"
        },
        {
          key: "jitter",
          title: "Jitter",
          videoOnly: false,
          receiverOnly: true,
          unit: "milliseconds"
        },
        {
          key: "roundTripTime",
          title: "RTT",
          videoOnly: false,
          senderOnly: true,
          unit: "milliseconds"
        }
      ]
    };
  },
  props: {
    id: null,
    appId: null
  },
  mounted() {
    this.getConnectionStats();
  },
  beforeDestroy() {
    // Clean up any pending scheduled notification...
    // Otherwise it will fire even when other Vues are mounted.
    if (this.freshnessIntervalId) {
      clearTimeout(this.freshnessIntervalId);
    }

    // Ensure the connection stats polling will
    // not continue to run.
    this.connectionStatsRunning = false;
  },
  computed: {
    backgroundColour: function() {
      var colour = this.hsl_col_perc(this.quality, this.red, this.green); // Red -> Green - "If the women don't find you handsome ..."
      return `background-color: ${colour};`;
    },
    nonVideoCharts: function() {
      return this.charts.filter(c => {
        return !c.videoOnly;
      });
    },
    quality: function() {
      const pL = this.lastPacketsLostValue;
      const pR = this.lastPacketsReceivedValue;
      var weightedLoss = (pL / (pR + pL)) * 3.33; // Ask Tim (seriously).

      if (isNaN(weightedLoss)) {
        weightedLoss = 0;
      }

      return Math.min(100, Math.ceil((1 - weightedLoss) * 100));
    }
  },
  methods: {
    notifyDataIsStale: function() {
      this.$notify({
        message: `No telemetry data has been received for ${this
          .freshnessTimerInterval / 1000} seconds.`,
        icon: "info",
        type: "info",
        horizontalAlign: "center",
        verticalAlign: "top"
      });
    },
    hsl_col_perc: function(percent, start, end) {
      var a = percent / 100,
        b = (end - start) * a,
        c = b + start;

      // Return a CSS HSL string
      return "hsl(" + c + ", 80%, 50%)";
    },
    getConnectionStats: function() {
      // Set to false on component destroy
      // which will prevent the connection
      // stat interval from continuously
      // being created after destory.
      if (!this.connectionStatsRunning) {
        return;
      }

      var me = this;

      // Schedule a notification that our data is stale.
      if (!this.freshnessIntervalId) {
        this.freshnessIntervalId = setInterval(
          this.notifyDataIsStale,
          this.freshnessTimerInterval
        );
      }
      /*
      We do not go through the store here by design.
      This data is not shared across components and likely never will be,
      so I felt there was no need for the added complexity.

      Note: this call returns the latest stats event for the connection.
      We have no idea if there is a new stats event in BigQuery since we last polled.
      We may have already seen this event and it is up to us to throw it out if we have.
      */
      api
        .getEntity(api.props.endPoints.dashboardQuery, null, [
          { k: "id", v: this.id },
          { k: "applicationId", v: this.appId },
          { k: "tag", v: this.queryTag },
          { k: "startTimestamp", v: this.lastTimestamp },
          {
            k: "endTimestamp",
            v: this.$store.getters.sqlTimestampGenerator(0)
          },
          { k: "server", v: false } // TODO: we need to handle server side stats ... but client only for the IRAP demo.
        ])
        .then((series) /* stats event results */ => {
          if (!series) {
            return; // Should be impossible.
          }

          series.forEach((se /* stats event */, index) => {
            // Compare to the last event and ignore if timestamp is identical.
            if (me.lastTimestamp === se.timestamp) {
              return; // We have already processed this stats svent.
            }

            // We have fresh data... cancel any pendiing scheduled notification.
            if (me.freshnessIntervalId) {
              clearTimeout(me.freshnessIntervalId);
              me.freshnessIntervalId = null;
            }

            // Parse the result and stick the right data into the right chart.
            me.supportedStreamTypes.forEach(type => {
              var senderReport = null;
              var receiverReport = null;
              var streamKey =
                type === "audio" ? "audioStreams" : "videoStreams";
              if (se[streamKey] && se[streamKey].length) {
                var streamStats = se[streamKey][0]; // For now, there will only ever be one stream in this array, so hard-coding is fine.
                if (streamStats.senders && streamStats.senders.length) {
                  senderReport = streamStats.senders[0].report; // For now, there will only ever be one sender in this array, so hard-coding is fine.
                }
                if (streamStats.receivers && streamStats.receivers.length) {
                  receiverReport = streamStats.receivers[0].report; // For now, there will only ever be one receiver in this array, so hard-coding is fine.
                }
              }

              me.parseStatsReport(
                type,
                se.timestamp,
                senderReport,
                receiverReport,
                index === series.length - 1
              );
            });

            // Update our last recorded timestamp for the stats event we just parsed.
            me.lastTimestamp = se.timestamp;
          });

          // Initialize all of our charts (if necessary).
          if (!me.bootstrapped) {
            // Init all charts based off of coalesced initial data series.
            me.charts.forEach(c => {
              if (me.$refs[`video-${c.key}`]) {
                me.$refs[`video-${c.key}`][0].bootstrap();
              }
              if (me.$refs[`audio-${c.key}`]) {
                me.$refs[`audio-${c.key}`][0].bootstrap();
              }
            });
            me.bootstrapped = true;
          }

          /*
          Poll recursively for active connection stats.
          We need to ensure we only make a request when the last one has been handled.
          We do not want them stacking up (race condition). So, we chain requests using
          `setTimeout` instead of `setInterval`.
          */
          setTimeout(me.getConnectionStats, me.connectionStatsDelay);
        })
        .catch(() => {
          // TODO: Should we do something here? The user has already been notified of the error.
        });
    },
    parseStatsReport: function(
      streamType,
      timestamp,
      senderReport,
      receiverReport,
      redraw
    ) {
      if (!senderReport && !receiverReport) {
        return; // Shouldn't be possible.
      }

      var me = this;
      var x = new Date(timestamp).getTime();
      this.charts.forEach(c => {
        // If this is a `videoOnly` chart, but we are not looking at a video stream then just continue on.
        if (c.videoOnly && streamType !== "video") {
          return;
        }

        if (
          receiverReport &&
          Number.isInteger(receiverReport.packetsLost) &&
          Number.isInteger(receiverReport.packetsReceived)
        ) {
          me.updatePacketStats(
            receiverReport.packetsLost,
            receiverReport.packetsReceived
          );
        }

        if (senderReport && !c.receiverOnly) {
          me.$refs[`${streamType}-${c.key}`][0].addPoint(
            "sender",
            x,
            senderReport[c.key],
            redraw
          );
        }

        if (receiverReport && !c.senderOnly) {
          me.$refs[`${streamType}-${c.key}`][0].addPoint(
            "receiver",
            x,
            receiverReport[c.key],
            redraw
          );
        }
      });
    },
    updatePacketStats(packetsLost, packetsReceived) {
      this.lastPacketsLostValue = packetsLost;
      this.lastPacketsReceivedValue = packetsReceived;
    }
  }
};
</script>
<style lang="scss" scoped>
#progress {
  position: absolute;
  right: 0;
  height: 50%;
  line-height: 1.2em;
  font-size: 1.2em;
}
</style>
